import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, getQueryFn, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { PageHeader } from "@/components/shared/page-header";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { DataTable, type ColumnDef } from "@/components/shared/data-table";
import { formatDateTime } from "@/lib/format";
import { Cable, Github, Link2, MessageSquareText, Workflow } from "lucide-react";

interface HighLevelSummary {
  connected: boolean;
  totalOpportunities: number;
  wonOpportunities: number;
  linkedOpportunities: number;
}

interface HighLevelOpportunity {
  id: string;
  name: string;
  companyName: string | null;
  stageName: string | null;
  contactEmail: string | null;
  isWon: boolean;
  linked: boolean;
  clientId: string | null;
}

interface GithubRepository {
  id: string;
  fullName: string;
  defaultBranch: string | null;
  active: boolean;
  createdAt: string;
}

interface GithubActivityRow {
  id: string;
  eventType: string;
  title: string;
  authorLogin: string | null;
  occurredAt: string;
  repository: GithubRepository;
  workItem: { id: string; title: string } | null;
  projectTask: { id: string; title: string } | null;
}

interface SlackConnection {
  id: string;
  provider: "slack";
  status: "disconnected" | "connected" | "error";
  displayName: string | null;
  botTokenConfigured?: boolean;
  config: {
    webhookUrl?: string;
    channelName?: string | null;
    alertsEnabled?: boolean;
    botTokenStatus?: "ok" | "invalid" | "missing_scope";
  } | null;
}

interface ProductiveSettings {
  configured: boolean;
  status: "disconnected" | "connected" | "error";
  displayName: string;
  lastSyncedAt: string | null;
  lastSummary: {
    companiesFetched?: number;
    companiesCreated?: number;
    companiesUpdated?: number;
    budgetsOpen?: number;
    servicesSynced?: number;
  } | null;
  envVars: string[];
}

interface ProductiveBudgetRow {
  id: string;
  productiveBudgetId: string;
  name: string;
  status: "open" | "closed";
  clientId: string | null;
  clientName: string | null;
  isRecurring: boolean;
  lastSyncedAt: string;
}

export default function AdminIntegrationsPage() {
  const { toast } = useToast();
  const [, navigate] = useLocation();

  const { data: highLevelSummary } = useQuery<HighLevelSummary>({
    queryKey: ["/api/integrations/highlevel/summary"],
    queryFn: getQueryFn({ on401: "throw" }),
  });
  const { data: opportunities, isLoading: opportunitiesLoading } = useQuery<
    HighLevelOpportunity[]
  >({
    queryKey: ["/api/integrations/highlevel/opportunities"],
    queryFn: getQueryFn({ on401: "throw" }),
  });
  const { data: repositories, isLoading: repositoriesLoading } = useQuery<
    GithubRepository[]
  >({
    queryKey: ["/api/github/repositories"],
    queryFn: getQueryFn({ on401: "throw" }),
  });
  const { data: activity, isLoading: activityLoading } = useQuery<
    GithubActivityRow[]
  >({
    queryKey: ["/api/github/activity"],
    queryFn: getQueryFn({ on401: "throw" }),
  });
  const { data: slackConnection } = useQuery<SlackConnection | null>({
    queryKey: ["/api/integrations/slack/settings"],
    queryFn: getQueryFn({ on401: "throw" }),
  });
  const { data: productiveSettings } = useQuery<ProductiveSettings>({
    queryKey: ["/api/integrations/productive/settings"],
    queryFn: getQueryFn({ on401: "throw" }),
  });
  const { data: productiveBudgets = [], isLoading: productiveBudgetsLoading } =
    useQuery<ProductiveBudgetRow[]>({
      queryKey: ["/api/integrations/productive/budgets"],
      queryFn: getQueryFn({ on401: "throw" }),
    });

  const [locationId, setLocationId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [repoOwner, setRepoOwner] = useState("");
  const [repoName, setRepoName] = useState("");
  const [repoDefaultBranch, setRepoDefaultBranch] = useState("main");
  const [repoWebhookSecret, setRepoWebhookSecret] = useState("");
  const [slackWebhookUrl, setSlackWebhookUrl] = useState("");
  const [slackChannelName, setSlackChannelName] = useState("#client-service");
  const [slackAlertsEnabled, setSlackAlertsEnabled] = useState(true);
  const [slackBotToken, setSlackBotToken] = useState("");
  const [slackBotTokenTouched, setSlackBotTokenTouched] = useState(false);

  useEffect(() => {
    if (!slackConnection?.config) return;

    setSlackWebhookUrl(slackConnection.config.webhookUrl || "");
    setSlackChannelName(slackConnection.config.channelName || "#client-service");
    setSlackAlertsEnabled(slackConnection.config.alertsEnabled !== false);
    setSlackBotToken(slackConnection.botTokenConfigured ? "********" : "");
    setSlackBotTokenTouched(false);
  }, [slackConnection]);

  const connectHighLevelMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/integrations/highlevel/connect", {
        locationId,
        apiKey,
        webhookSecret,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/integrations/highlevel/summary"],
      });
      toast({ title: "HighLevel connected" });
      setApiKey("");
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to connect HighLevel",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const createClientContractMutation = useMutation({
    mutationFn: async (opportunityId: string) => {
      const res = await apiRequest(
        "POST",
        `/api/highlevel/opportunities/${opportunityId}/create-client-contract`,
      );
      return res.json();
    },
    onSuccess: (data: { client?: { id: string } }) => {
      queryClient.invalidateQueries({
        queryKey: ["/api/integrations/highlevel/opportunities"],
      });
      toast({ title: "Client and contract created" });
      if (data.client?.id) {
        navigate(`/clients/${data.client.id}`);
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to create client/contract",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const registerRepoMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/github/repositories", {
        owner: repoOwner,
        name: repoName,
        defaultBranch: repoDefaultBranch,
        webhookSecret: repoWebhookSecret || undefined,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/github/repositories"] });
      toast({ title: "Repository saved" });
      setRepoOwner("");
      setRepoName("");
      setRepoWebhookSecret("");
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to save repository",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const saveSlackMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/integrations/slack/connect", {
        webhookUrl: slackWebhookUrl,
        channelName: slackChannelName || undefined,
        alertsEnabled: slackAlertsEnabled,
        botToken: slackBotTokenTouched
          ? slackBotToken
          : slackConnection?.botTokenConfigured
            ? "********"
            : slackBotToken,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/integrations/slack/settings"],
      });
      toast({ title: "Slack settings saved" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to save Slack settings",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const testSlackMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/integrations/slack/test");
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Test Slack alert sent" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to send test Slack alert",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const productiveSyncMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/integrations/productive/sync", {});
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/integrations/productive/settings"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/integrations/productive/budgets"],
      });
      toast({ title: "Productive sync completed" });
    },
    onError: (error: Error) => {
      toast({
        title: "Productive sync failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const productiveBudgetColumns: ColumnDef<ProductiveBudgetRow>[] = [
    {
      key: "name",
      header: "Budget",
      cell: (row) => (
        <div>
          <div className="font-medium">{row.name}</div>
          <div className="text-xs text-muted-foreground">
            {row.clientName || "Unlinked company"}
          </div>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (row) => (row.status === "open" ? "Active" : "Inactive"),
    },
    {
      key: "isRecurring",
      header: "Recurring",
      cell: (row) => (row.isRecurring ? "Yes" : "One-off"),
    },
  ];

  const opportunityColumns: ColumnDef<HighLevelOpportunity>[] = [
    {
      key: "name",
      header: "Opportunity",
      cell: (row) => (
        <div>
          <div className="font-medium">{row.name}</div>
          <div className="text-xs text-muted-foreground">
            {row.companyName || row.contactEmail || "No company info"}
          </div>
        </div>
      ),
    },
    {
      key: "stage",
      header: "Stage",
      cell: (row) => row.stageName || "Unknown",
    },
    {
      key: "status",
      header: "Status",
      cell: (row) => (row.linked ? "Linked" : row.isWon ? "Won" : "Open"),
    },
    {
      key: "action",
      header: "Action",
      cell: (row) =>
        row.linked ? (
          <Button
            variant="outline"
            size="sm"
            onClick={(event) => {
              event.stopPropagation();
              if (row.clientId) navigate(`/clients/${row.clientId}`);
            }}
          >
            View Client
          </Button>
        ) : (
          <Button
            size="sm"
            disabled={!row.isWon || createClientContractMutation.isPending}
            onClick={(event) => {
              event.stopPropagation();
              createClientContractMutation.mutate(row.id);
            }}
          >
            Create Client/Contract
          </Button>
        ),
    },
  ];

  const repositoryColumns: ColumnDef<GithubRepository>[] = [
    {
      key: "fullName",
      header: "Repository",
      cell: (row) => (
        <div>
          <div className="font-medium">{row.fullName}</div>
          <div className="text-xs text-muted-foreground">
            Default branch: {row.defaultBranch || "not set"}
          </div>
        </div>
      ),
    },
    {
      key: "active",
      header: "Status",
      cell: (row) => (row.active ? "Active" : "Inactive"),
    },
    {
      key: "createdAt",
      header: "Added",
      cell: (row) => formatDateTime(row.createdAt),
    },
  ];

  const activityColumns: ColumnDef<GithubActivityRow>[] = [
    {
      key: "title",
      header: "Activity",
      cell: (row) => (
        <div>
          <div className="font-medium">{row.title}</div>
          <div className="text-xs text-muted-foreground">
            {row.repository.fullName}
          </div>
        </div>
      ),
    },
    {
      key: "target",
      header: "Linked To",
      cell: (row) =>
        row.workItem?.title ||
        row.projectTask?.title ||
        "Repository activity only",
    },
    {
      key: "authorLogin",
      header: "Author",
      cell: (row) => row.authorLogin || "github",
    },
    {
      key: "occurredAt",
      header: "Occurred",
      cell: (row) => formatDateTime(row.occurredAt),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Integrations"
        description="Connect CRM and engineering activity to client delivery."
      />

      <div className="grid gap-4 md:grid-cols-3">
        <MetricCard
          title="Connected"
          value={highLevelSummary?.connected ? "CRM live" : "Not connected"}
          icon={Cable}
        />
        <MetricCard
          title="Won Opportunities"
          value={highLevelSummary?.wonOpportunities ?? 0}
          icon={Workflow}
        />
        <MetricCard
          title="Linked Opportunities"
          value={highLevelSummary?.linkedOpportunities ?? 0}
          icon={Link2}
        />
      </div>

      <Tabs defaultValue="highlevel" className="space-y-6">
        <TabsList>
          <TabsTrigger value="highlevel">HighLevel</TabsTrigger>
          <TabsTrigger value="github">GitHub</TabsTrigger>
          <TabsTrigger value="slack">Slack</TabsTrigger>
          <TabsTrigger value="productive">Productive</TabsTrigger>
        </TabsList>

        <TabsContent value="highlevel" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>HighLevel Connection</CardTitle>
              <CardDescription>
                Store the CRM link config and receive won-opportunity webhooks.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Location ID</Label>
                <Input
                  value={locationId}
                  onChange={(event) => setLocationId(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Webhook Secret</Label>
                <Input
                  value={webhookSecret}
                  onChange={(event) => setWebhookSecret(event.target.value)}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>API Key</Label>
                <Input
                  type="password"
                  value={apiKey}
                  onChange={(event) => setApiKey(event.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <Button
                  onClick={() => connectHighLevelMutation.mutate()}
                  disabled={connectHighLevelMutation.isPending}
                >
                  Save HighLevel Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          <DataTable
            columns={opportunityColumns}
            data={opportunities}
            isLoading={opportunitiesLoading}
            rowKey={(row) => row.id}
            emptyIcon={Workflow}
            emptyTitle="No opportunities received"
            emptyDescription="HighLevel won-opportunity webhooks will appear here."
          />
        </TabsContent>

        <TabsContent value="github" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Tracked Repository</CardTitle>
              <CardDescription>
                Register repositories so webhook activity can attach to work items and project tasks.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Owner</Label>
                <Input
                  value={repoOwner}
                  onChange={(event) => setRepoOwner(event.target.value)}
                  placeholder="heyhihello"
                />
              </div>
              <div className="space-y-2">
                <Label>Repository</Label>
                <Input
                  value={repoName}
                  onChange={(event) => setRepoName(event.target.value)}
                  placeholder="hms-unified"
                />
              </div>
              <div className="space-y-2">
                <Label>Default Branch</Label>
                <Input
                  value={repoDefaultBranch}
                  onChange={(event) => setRepoDefaultBranch(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Webhook Secret</Label>
                <Input
                  value={repoWebhookSecret}
                  onChange={(event) => setRepoWebhookSecret(event.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <Button
                  onClick={() => registerRepoMutation.mutate()}
                  disabled={registerRepoMutation.isPending}
                >
                  Save Repository
                </Button>
              </div>
            </CardContent>
          </Card>

          <DataTable
            columns={repositoryColumns}
            data={repositories}
            isLoading={repositoriesLoading}
            rowKey={(row) => row.id}
            emptyIcon={Github}
            emptyTitle="No repositories configured"
            emptyDescription="Add a repository to start storing GitHub activity."
          />

          <DataTable
            columns={activityColumns}
            data={activity}
            isLoading={activityLoading}
            rowKey={(row) => row.id}
            emptyIcon={Github}
            emptyTitle="No GitHub activity yet"
            emptyDescription="Webhook events linked to work items or tasks will appear here."
          />
        </TabsContent>

        <TabsContent value="slack" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Slack Alerts</CardTitle>
              <CardDescription>
                Work request alerts use the incoming webhook. File upload
                routing uses the bot token and posts with chat.postMessage.
                The webhook stays the default channel and the fallback.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2 md:col-span-2">
                <Label>Bot token</Label>
                <Input
                  type="password"
                  value={slackBotToken}
                  onChange={(event) => {
                    setSlackBotTokenTouched(true);
                    setSlackBotToken(event.target.value);
                  }}
                  placeholder="xoxb-…"
                  autoComplete="off"
                />
                <p className="text-xs text-muted-foreground">
                  Bot User OAuth Token from the HMS Slack app. Leave the masked
                  value to keep the saved token, or clear the field to remove
                  it. The token is not shown again after you save.
                  {slackConnection?.config?.botTokenStatus === "invalid"
                    ? " Slack rejected this token. Paste a new one."
                    : slackConnection?.config?.botTokenStatus === "missing_scope"
                      ? " The Slack app is missing a required scope. Reinstall it from the manifest, then paste the token again."
                      : ""}
                </p>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Incoming Webhook URL</Label>
                <Input
                  type="password"
                  value={slackWebhookUrl}
                  onChange={(event) => setSlackWebhookUrl(event.target.value)}
                  placeholder="https://hooks.slack.com/services/..."
                />
              </div>
              <div className="space-y-2">
                <Label>Channel Label</Label>
                <Input
                  value={slackChannelName}
                  onChange={(event) => setSlackChannelName(event.target.value)}
                  placeholder="#client-service"
                />
              </div>
              <div className="space-y-2">
                <Label>Alerts Enabled</Label>
                <div className="flex h-10 items-center justify-between rounded-md border border-input px-3">
                  <span className="text-sm text-muted-foreground">
                    {slackAlertsEnabled ? "Enabled" : "Disabled"}
                  </span>
                  <Switch
                    checked={slackAlertsEnabled}
                    onCheckedChange={setSlackAlertsEnabled}
                  />
                </div>
              </div>
              <div className="rounded-xl border border-border bg-muted/30 p-4 text-sm text-muted-foreground md:col-span-2">
                <div className="flex items-center gap-2 font-medium text-foreground">
                  <MessageSquareText className="h-4 w-4 text-primary" />
                  Current status
                </div>
                <p className="mt-2">
                  {slackConnection?.status === "connected"
                    ? `Slack is connected${slackConnection.config?.channelName ? ` for ${slackConnection.config.channelName}` : ""}.`
                    : "Slack is not connected yet."}
                </p>
              </div>
              <div className="flex gap-3 md:col-span-2">
                <Button
                  onClick={() => saveSlackMutation.mutate()}
                  disabled={
                    saveSlackMutation.isPending || slackWebhookUrl.trim().length === 0
                  }
                >
                  Save Slack Settings
                </Button>
                <Button
                  variant="outline"
                  onClick={() => testSlackMutation.mutate()}
                  disabled={testSlackMutation.isPending || !slackConnection}
                >
                  Send Test Alert
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="productive" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Productive</CardTitle>
              <CardDescription>
                Productive stays the source of truth. HMS pulls companies and
                open budgets/services so the public request form can attach to
                live work. Tokens live in env, not the database.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm">
                {productiveSettings?.configured
                  ? "API credentials are configured on the server."
                  : "Set PRODUCTIVE_API_TOKEN and PRODUCTIVE_ORG_ID on the server, then sync."}
              </p>
              {productiveSettings?.lastSyncedAt && (
                <p className="text-sm text-muted-foreground">
                  Last sync {formatDateTime(productiveSettings.lastSyncedAt)}
                  {productiveSettings.lastSummary
                    ? ` — ${productiveSettings.lastSummary.companiesFetched ?? 0} companies, ${productiveSettings.lastSummary.budgetsOpen ?? 0} open budgets, ${productiveSettings.lastSummary.servicesSynced ?? 0} services`
                    : ""}
                </p>
              )}
              <Button
                onClick={() => productiveSyncMutation.mutate()}
                disabled={
                  productiveSyncMutation.isPending ||
                  !productiveSettings?.configured
                }
              >
                Sync now
              </Button>
            </CardContent>
          </Card>

          <DataTable
            columns={productiveBudgetColumns}
            data={productiveBudgets}
            isLoading={productiveBudgetsLoading}
            rowKey={(row) => row.id}
            emptyIcon={Workflow}
            emptyTitle="No Productive budgets synced"
            emptyDescription="Run a sync after setting PRODUCTIVE_API_TOKEN and PRODUCTIVE_ORG_ID."
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MetricCard({
  title,
  value,
  icon: Icon,
}: {
  title: string;
  value: string | number;
  icon: typeof Cable;
}) {
  return (
    <Card>
      <CardContent className="flex items-center justify-between p-6">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-semibold">{value}</p>
        </div>
        <Icon className="h-5 w-5 text-primary" />
      </CardContent>
    </Card>
  );
}
